/**
 * Batch #14.16 — Portail Client : Bureau à domicile (T777 résumé annuel SIMPLE).
 *
 * Format AGRÉGÉ annuel (vs l'ancien T777Tab.jsx qui était ligne-par-ligne).
 * Le client remplit UN seul formulaire par année fiscale avec les totaux
 * annuels par catégorie de dépense, puis clique "Envoyer à mon comptable".
 *
 * Workflow :
 *   1. Le client ouvre la tuile → on charge `GET /portal/t777-summary?tax_year=YYYY`
 *   2. S'il a un brouillon → préremplir le formulaire
 *   3. Modifications → autosave (POST /portal/t777-summary)
 *   4. Click "Envoyer" → POST /portal/t777-summary/{id}/submit
 *      → verrouille + envoie un email au cabinet + apparaît dans dashboard admin
 *   5. Après envoi → vue read-only avec accusé de réception
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Home, Save, Send, CheckCircle2, AlertCircle, Loader2,
  Lock, FileText, Calculator,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders } from "../helpers.js";

const CURRENT_YEAR = new Date().getFullYear();

const DEFAULTS = {
  home_total_sqft: "",
  office_sqft: "",
  hours_per_week: "",
  electricity: "",
  heating: "",
  internet: "",
  rent: "",
  mortgage_interest: "",
  property_tax: "",
  home_insurance: "",
  maintenance: "",
  other_expenses: "",
  other_expenses_label: "",
  notes: "",
};

function fmt(n) {
  const v = Number(n || 0);
  return v.toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
}

function Field({ label, name, value, onChange, type = "number", step = "0.01", suffix, placeholder, disabled, hint, textarea = false, testid }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-600">
        {label}
      </label>
      <div className="relative">
        {textarea ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            rows={3}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            data-testid={testid}
          />
        ) : (
          <input
            type={type}
            step={type === "number" ? step : undefined}
            min={type === "number" ? "0" : undefined}
            value={value === null || value === undefined ? "" : value}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${suffix ? "pr-10" : ""}`}
            data-testid={testid}
          />
        )}
        {suffix && !textarea && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <p className="text-[11px] text-slate-400">{hint}</p>
      )}
    </div>
  );
}

export default function T777SimpleTab({ primaryColor = "#072354" }) {
  const [year] = useState(CURRENT_YEAR);
  const [form, setForm] = useState(DEFAULTS);
  const [summaryId, setSummaryId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Chargement initial
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/t777-summary?tax_year=${year}`, {
        headers: portalHeaders(),
      });
      const s = r.data?.summary;
      if (s) {
        setForm({
          home_total_sqft: s.home_total_sqft ?? "",
          office_sqft: s.office_sqft ?? "",
          hours_per_week: s.hours_per_week ?? "",
          electricity: s.electricity ?? "",
          heating: s.heating ?? "",
          internet: s.internet ?? "",
          rent: s.rent ?? "",
          mortgage_interest: s.mortgage_interest ?? "",
          property_tax: s.property_tax ?? "",
          home_insurance: s.home_insurance ?? "",
          maintenance: s.maintenance ?? "",
          other_expenses: s.other_expenses ?? "",
          other_expenses_label: s.other_expenses_label ?? "",
          notes: s.notes ?? "",
        });
        setSummaryId(s.id);
        setSubmitted(!!s.submitted);
        setSubmittedAt(s.submitted_at || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Totaux calculés côté front (preview)
  const totals = useMemo(() => {
    const expenseFields = [
      "electricity", "heating", "internet", "rent", "mortgage_interest",
      "property_tax", "home_insurance", "maintenance", "other_expenses",
    ];
    const total = expenseFields.reduce((acc, k) => acc + Number(form[k] || 0), 0);
    const home = Number(form.home_total_sqft || 0);
    const office = Number(form.office_sqft || 0);
    const pct = home > 0 ? (office / home) * 100 : 0;
    return {
      total: Math.round(total * 100) / 100,
      space_pct: Math.round(pct * 100) / 100,
    };
  }, [form]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
  };

  // Construction du payload (cast en numbers)
  const buildPayload = () => {
    const numFields = [
      "home_total_sqft", "office_sqft", "hours_per_week",
      "electricity", "heating", "internet", "rent", "mortgage_interest",
      "property_tax", "home_insurance", "maintenance", "other_expenses",
    ];
    const payload = { tax_year: year };
    numFields.forEach((k) => { payload[k] = Number(form[k] || 0); });
    payload.other_expenses_label = form.other_expenses_label || "";
    payload.notes = form.notes || "";
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/portal/t777-summary`, buildPayload(), {
        headers: portalHeaders(),
      });
      if (r.data?.summary?.id) {
        setSummaryId(r.data.summary.id);
      }
      setDirty(false);
      toast.success("Brouillon enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Sauvegarde d'abord pour s'assurer que les derniers changements sont persistés
    if (!summaryId || dirty) {
      try {
        const r = await axios.post(`${API}/portal/t777-summary`, buildPayload(), {
          headers: portalHeaders(),
        });
        if (!summaryId && r.data?.summary?.id) {
          setSummaryId(r.data.summary.id);
        }
        setDirty(false);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
        return;
      }
    }
    if (!window.confirm(
      `Envoyer votre déclaration Bureau à domicile ${year} à votre comptable ?\n\n` +
      `Total des dépenses : ${fmt(totals.total)}\n` +
      `Superficie bureau  : ${totals.space_pct.toFixed(2)} %\n\n` +
      `⚠️ Une fois envoyée, vous ne pourrez plus la modifier sans contacter le cabinet.`
    )) return;

    setSubmitting(true);
    try {
      const idToUse = summaryId || (await axios.get(`${API}/portal/t777-summary?tax_year=${year}`, { headers: portalHeaders() })).data?.summary?.id;
      if (!idToUse) {
        toast.error("Veuillez d'abord enregistrer un brouillon");
        return;
      }
      await axios.post(`${API}/portal/t777-summary/${idToUse}/submit`, null, {
        headers: portalHeaders(),
      });
      toast.success("Déclaration envoyée à votre équipe !");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="t777-simple-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  const isLocked = submitted;

  return (
    <div className="space-y-5" data-testid="t777-simple-tab">
      {/* Bandeau d'intro */}
      <div
        className="rounded-2xl p-5 text-white"
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #2877D1 100%)` }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Home className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold">Bureau à domicile — Année {year}</h3>
            <p className="text-xs opacity-90">Déclaration T777 — formulaire annuel simplifié</p>
          </div>
        </div>
        <p className="text-xs opacity-85 leading-relaxed">
          Indiquez les totaux annuels de vos dépenses liées à l'usage de votre domicile pour le télétravail.
          Lorsque vous avez terminé, cliquez sur <strong>« Envoyer à mon équipe »</strong> en bas du formulaire.
        </p>
      </div>

      {/* Bandeau "déjà envoyé" */}
      {isLocked && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="t777-simple-submitted-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-900">Déclaration envoyée à votre équipe</p>
            <p className="text-emerald-800 text-xs mt-1">
              Envoyée le {submittedAt ? new Date(submittedAt).toLocaleDateString("fr-CA", { dateStyle: "long" }) : "—"}.
              Pour la modifier, veuillez contacter votre cabinet.
            </p>
          </div>
        </div>
      )}

      {/* Section 1 : Logement */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="t777-section-home">
        <h4 className="font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: primaryColor }} />
          1. Mesures du logement
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field
            label="Superficie totale du logement"
            name="home_total_sqft"
            value={form.home_total_sqft}
            onChange={handleChange}
            suffix="pi²"
            placeholder="1000"
            disabled={isLocked}
            testid="t777-home-sqft"
          />
          <Field
            label="Superficie du bureau"
            name="office_sqft"
            value={form.office_sqft}
            onChange={handleChange}
            suffix="pi²"
            placeholder="100"
            disabled={isLocked}
            testid="t777-office-sqft"
          />
          <Field
            label="Heures travaillées / semaine"
            name="hours_per_week"
            value={form.hours_per_week}
            onChange={handleChange}
            suffix="h"
            placeholder="40"
            disabled={isLocked}
            testid="t777-hours"
          />
        </div>
        {totals.space_pct > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-900 flex items-center gap-2" data-testid="t777-pct-preview">
            <Calculator className="w-4 h-4" />
            Votre bureau représente <strong>{totals.space_pct.toFixed(2)} %</strong> de votre logement.
          </div>
        )}
      </section>

      {/* Section 2 : Dépenses */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="t777-section-expenses">
        <h4 className="font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: primaryColor }} />
          2. Dépenses annuelles totales
        </h4>
        <p className="text-xs text-slate-500 -mt-2">
          Montants annuels (CAD) pour l'année complète {year}. Laissez vide si non applicable.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Électricité" name="electricity" value={form.electricity} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-electricity" />
          <Field label="Chauffage" name="heating" value={form.heating} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-heating" />
          <Field label="Internet" name="internet" value={form.internet} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-internet" />
          <Field label="Loyer" name="rent" value={form.rent} onChange={handleChange} suffix="$" disabled={isLocked} hint="Si vous êtes locataire" testid="t777-rent" />
          <Field label="Intérêts hypothécaires" name="mortgage_interest" value={form.mortgage_interest} onChange={handleChange} suffix="$" disabled={isLocked} hint="Si vous êtes propriétaire" testid="t777-mortgage" />
          <Field label="Taxes foncières" name="property_tax" value={form.property_tax} onChange={handleChange} suffix="$" disabled={isLocked} hint="Si vous êtes propriétaire" testid="t777-property-tax" />
          <Field label="Assurance habitation" name="home_insurance" value={form.home_insurance} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-insurance" />
          <Field label="Entretien et réparations" name="maintenance" value={form.maintenance} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-maintenance" />
          <Field label="Autres dépenses" name="other_expenses" value={form.other_expenses} onChange={handleChange} suffix="$" disabled={isLocked} testid="t777-other" />
        </div>
        {Number(form.other_expenses || 0) > 0 && (
          <Field
            label="Précisez la nature des autres dépenses"
            name="other_expenses_label"
            value={form.other_expenses_label}
            onChange={handleChange}
            type="text"
            placeholder="Ex : papeterie, ligne téléphonique professionnelle…"
            disabled={isLocked}
            testid="t777-other-label"
          />
        )}

        {/* Récap total */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="t777-total-preview">
          <span className="text-sm font-semibold text-slate-700">Total dépenses annuelles</span>
          <span className="text-lg font-bold" style={{ color: primaryColor }}>
            {fmt(totals.total)}
          </span>
        </div>
      </section>

      {/* Section 3 : Notes */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="t777-section-notes">
        <h4 className="font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: primaryColor }} />
          3. Notes ou questions pour votre comptable (facultatif)
        </h4>
        <Field
          label="Notes"
          name="notes"
          value={form.notes}
          onChange={handleChange}
          type="text"
          textarea
          placeholder="Précisions sur certaines dépenses, changement de situation, questions…"
          disabled={isLocked}
          testid="t777-notes"
        />
      </section>

      {/* Actions */}
      {!isLocked && (
        <div className="sticky bottom-0 bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg" data-testid="t777-simple-actions">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            {dirty
              ? "Vous avez des modifications non enregistrées"
              : summaryId
              ? "Brouillon enregistré — vous pouvez continuer plus tard"
              : "Vos données sont enregistrées automatiquement"}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="t777-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer le brouillon
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: primaryColor }}
              data-testid="t777-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer à mon comptable
            </button>
          </div>
        </div>
      )}

      {isLocked && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm text-slate-600 flex items-center gap-2" data-testid="t777-simple-locked">
          <Lock className="w-4 h-4" />
          Formulaire verrouillé après envoi. Pour toute modification, contactez votre cabinet.
        </div>
      )}
    </div>
  );
}
